import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../../config";
import "./UpcomingBirthdays.css";

const PROFILE_BASE = `${API_BASE_URL.replace("/api", "")}/uploads/profile/`;

// single theme accent for every employee card
const ACCENT = "#ff9b44";

const BALLOONS = [
  { color: "#ff9b44", left: "6%", size: 30, duration: 9, delay: 0 },
  { color: "#ffd166", left: "18%", size: 24, duration: 11, delay: 1.6 },
  { color: "#ffffff", left: "31%", size: 34, duration: 10, delay: 3.1 },
  { color: "#e4bd51", left: "47%", size: 22, duration: 12, delay: 0.8 },
  { color: "#ffc89b", left: "62%", size: 28, duration: 9.5, delay: 2.4 },
  { color: "#fc6075", left: "76%", size: 32, duration: 11.5, delay: 4.2 },
  { color: "#ffe08a", left: "89%", size: 26, duration: 10.5, delay: 1.2 },
];

// confetti that keeps falling over the whole widget
// drift = how far it slides sideways on the way down, op = max opacity
const FALLING = [
  { icon: "🎈", left: "3%", size: 20, duration: 13, delay: 0, drift: 24, op: 0.7 },
  { icon: "💖", left: "11%", size: 15, duration: 10, delay: 3.4, drift: -18, op: 0.55 },
  { icon: "🎂", left: "19%", size: 18, duration: 15, delay: 6.2, drift: 16, op: 0.6 },
  { icon: "🎉", left: "27%", size: 16, duration: 11.5, delay: 1.8, drift: -26, op: 0.6 },
  { icon: "🎁", left: "36%", size: 17, duration: 14, delay: 8.1, drift: 20, op: 0.5 },
  { icon: "💝", left: "44%", size: 14, duration: 9.5, delay: 4.6, drift: -14, op: 0.65 },
  { icon: "🧁", left: "52%", size: 19, duration: 12.5, delay: 2.2, drift: 22, op: 0.6 },
  { icon: "🎈", left: "60%", size: 16, duration: 10.5, delay: 7.3, drift: -20, op: 0.7 },
  { icon: "❤️", left: "68%", size: 13, duration: 13.5, delay: 5.1, drift: 18, op: 0.5 },
  { icon: "🎊", left: "75%", size: 17, duration: 11, delay: 9.4, drift: -22, op: 0.55 },
  { icon: "🎂", left: "83%", size: 15, duration: 14.5, delay: 3.9, drift: 14, op: 0.6 },
  { icon: "💗", left: "90%", size: 16, duration: 9.8, delay: 6.8, drift: -16, op: 0.6 },
  { icon: "🎈", left: "96%", size: 18, duration: 12, delay: 1.1, drift: 20, op: 0.65 },
];

const fullName = (r) => `${r.first_name || ""} ${r.last_name || ""}`.trim() || "-";

const initials = (r) =>
  `${(r.first_name || "").charAt(0)}${(r.last_name || "").charAt(0)}`.toUpperCase() || "?";

const prettyDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(`${iso}T00:00:00`);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" });
};

const whenLabel = (days) => {
  if (days <= 0) return "Today 🎉";
  if (days === 1) return "Tomorrow";
  return `In ${days} days`;
};

function Avatar({ row }) {
  const [broken, setBroken] = useState(false);
  const showImg = row.profile_picture && !broken;

  return (
    <div className={`ub-avatar${showImg ? "" : " is-initials"}`}>
      <span className="ub-hat" role="img" aria-label="party hat">
        🎈
      </span>
      {showImg ? (
        <img
          src={`${PROFILE_BASE}${row.profile_picture}`}
          alt={fullName(row)}
          onError={() => setBroken(true)}
        />
      ) : (
        initials(row)
      )}
    </div>
  );
}

export default function UpcomingBirthdays({ days = 7 }) {
  const [rows, setRows] = useState([]);
  const [range, setRange] = useState({ from: "", to: "" });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    const token = localStorage.getItem("authToken");

    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/upcomingBirthdays`, {
          params: { days },
          headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
        });
        if (!alive) return;
        setRows(data?.data || []);
        setRange({ from: data?.from || "", to: data?.to || "" });
      } catch (e) {
        console.error("upcoming birthdays failed:", e?.message || e);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [days]);

  return (
    <div className="card ub-card">
      <div className="ub-fall" aria-hidden="true">
        {FALLING.map((f, i) => (
          <span
            key={i}
            className="ub-fall-item"
            style={{
              left: f.left,
              animationDuration: `${f.duration}s`,
              animationDelay: `${f.delay}s`,
              "--ub-drift": `${f.drift}px`,
              "--ub-op": f.op,
            }}
          >
            <span className="ub-fall-icon" style={{ fontSize: f.size }}>
              {f.icon}
            </span>
          </span>
        ))}
      </div>

      <div className="ub-head">
        <div className="ub-balloons" aria-hidden="true">
          {BALLOONS.map((b, i) => (
            <span
              key={i}
              className="ub-balloon"
              style={{
                left: b.left,
                width: b.size,
                height: b.size * 1.3,
                background: b.color,
                animationDuration: `${b.duration}s`,
                animationDelay: `${b.delay}s`,
              }}
            />
          ))}
        </div>

        <div className="ub-head-inner">
          <div>
            <h3 className="ub-title">
              <span className="ub-cake" role="img" aria-label="birthday cake">
                🎂
              </span>
              Upcoming Birthdays
            </h3>
            <p className="ub-sub">
              Next {days} days
              {range.from && range.to ? ` · ${prettyDate(range.from)} – ${prettyDate(range.to)}` : ""}
            </p>
          </div>

          <span className="ub-count">
            🎈 {loading ? "…" : rows.length} {rows.length === 1 ? "Celebration" : "Celebrations"}
          </span>
        </div>
      </div>

      <div className="ub-body">
        {loading ? (
          <div className="ub-grid">
            {[0, 1, 2].map((i) => (
              <div key={i} className="ub-skeleton" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="ub-empty">
            <span role="img" aria-label="calendar">
              🗓️
            </span>
            No birthdays in the next {days} days.
          </div>
        ) : (
          <div className="ub-grid">
            {rows.map((r, i) => {
              const isToday = Number(r.days_left) <= 0;

              return (
                <div
                  key={`${r.employee_id}-${i}`}
                  className={`ub-item${isToday ? " is-today" : ""}`}
                  style={{ "--ub-accent": ACCENT }}
                >
                  <Avatar row={r} />

                  <div className="ub-info">
                    <h6 className="ub-name" title={fullName(r)}>
                      {fullName(r)}
                    </h6>

                    <div className="ub-meta">
                      <span className="ub-chip">ID: {r.employee_id || "-"}</span>
                      <span className="ub-chip ub-dept" title={r.department_name || "-"}>
                        {r.department_name || "No Department"}
                      </span>
                    </div>

                    <p className="ub-date">
                      <i className="fa fa-birthday-cake" aria-hidden="true" />
                      {prettyDate(r.birthday_date)}
                    </p>
                  </div>

                  <span className={`ub-when${isToday ? " is-today" : ""}`}>
                    {whenLabel(Number(r.days_left))}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
